import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn, concat } from '@ember/helper';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsTable,
  HdsBadge,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Flex, Time } from 'core/components';
import { optionForSourceState } from 'core/helpers';

/**
 *
 * `ServicePrincipalKeysListTable` renders a generic list of service principal keys, organization/project scope agnostic.
 *
 *
 * ```
 * <ServicePrincipal::Keys::ListTable
 *  @keys={{...}}
 *  @handleConfirmDeleteKey={{...}}
 * />
 * ```
 *
 * @class ServicePrincipalKeysListTable
 *
 */
export default class ServicePrincipalKeysListTable extends Component {
  /**
   * @argument keys
   * @type {Array.<Object>}
   * @required
   */
  /**
   * @argument handleConfirmDeleteKey
   * @type {Function}
   * @required
   */
  <template>
    <HdsTable
      data-test-service-principal-keys-list-table
      @caption={{t
        "components.page.access-control.service-principals.details.label.keys"
      }}
      @valign="middle"
    >
      <:head as |H|>
        <H.Tr>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.keys.table.headers.client-id"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.keys.table.headers.status"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.service-principals.details.keys.table.headers.created"
            }}
          </H.Th>
          {{#if (can "delete service-principal-key")}}
            <H.Th @width="85px">
              {{t
                "components.page.access-control.service-principals.details.keys.table.headers.actions"
              }}
            </H.Th>
          {{/if}}
        </H.Tr>
      </:head>
      <:body as |B|>
        {{#each @keys key="id" as |key index|}}
          <B.Tr data-test-service-principal-key-item>
            <B.Td data-test-service-principal-key-item-client-id>
              {{key.clientId}}
            </B.Td>
            <B.Td data-test-service-principal-key-item-state>
              {{#let
                (optionForSourceState "service_principal_key" key.state)
                as |option|
              }}
                <HdsBadge
                  @text={{t option.text}}
                  @color={{option.color}}
                  @icon={{option.icon}}
                  @isIconOnly={{option.isIconOnly}}
                  @size={{option.size}}
                  @type={{option.type}}
                />
              {{/let}}
            </B.Td>
            <B.Td data-test-service-principal-key-item-created-at>
              <Time @date={{key.createdAt}} />
            </B.Td>
            {{#if (can "delete service-principal-key")}}
              <B.Td>
                <Flex @justifyContent="end">
                  <HdsDropdown data-test-more-menu as |dd|>
                    <dd.ToggleIcon
                      @icon="more-horizontal"
                      @text={{t "components.form.more"}}
                      @hasChevron={{false}}
                      data-test-more-menu-trigger
                      data-test-service-principal-key-item-menu
                    />
                    <dd.Interactive
                      @color="critical"
                      @icon="trash"
                      @text={{t
                        "components.page.access-control.service-principals.details.actions.delete-key"
                      }}
                      id="modal-dialog-delete-open-button-{{index}}"
                      data-test-delete-service-principal-key-button
                      {{on
                        "click"
                        (fn
                          @handleConfirmDeleteKey
                          key
                          (concat "modal-dialog-delete-open-button-" index)
                        )
                      }}
                    />
                  </HdsDropdown>
                </Flex>
              </B.Td>
            {{/if}}
          </B.Tr>
        {{/each}}
      </:body>
    </HdsTable>
  </template>
}
